import { applyCanonicalNutrition } from "./utils/aiAction";
import { useState, useMemo, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import {
  Refrigerator, ChefHat, Globe, Check, X, Clock, Users,
  Star, Filter, Search, Bookmark, BookmarkCheck, Share2, Trash2, Sparkles, History, Moon, Sun,
  Camera, User, TrendingDown,
  Scale, HelpCircle,
  PieChart,
  Footprints, WifiOff, BookOpen,
  ShieldAlert, Award, Music2, Barcode, FlameKindling, Droplets, Info, Crown, Mail, Link2, Pill,
  Thermometer, Flame, UtensilsCrossed, AlertTriangle, ChevronDown,
} from "lucide-react";
import {
  languages, translations, fridgeIngredients, recipes, servingsLabel,
  type Language, type FoodCategory, type Recipe
} from "./data";
import { supabase } from "./supabase";
import { preservedImages, preservedImageUrl, cachedImageMap } from "./utils/preservedImages";
import { HealthScoreInlineCard, type HealthScoreResult } from "./HealthScoreTab";
import { StepsTab } from "./StepsTab";
import { useMusicPlayer } from "./useMusicPlayer";
import type { Session } from "@supabase/supabase-js";

import { AuthModal } from "./components/AuthModal";
import { MacroDonut } from "./components/MacroDonut";
import { RecipeCard } from "./components/RecipeCard";
import { AiRecipeCard } from "./components/AiRecipeCard";
import { WaterReminder } from "./components/WaterReminder";
import { getTrialInfo } from "./components/TrialGate";
import { isDisclaimerAccepted } from "./components/DisclaimerGate";
import { ConsentGate, checkConsentCurrent, clearStoredConsentVersion, getStoredConsentVersion } from "./components/ConsentGate";
import { getStoredLang, storeLang } from "./components/LanguagePicker";
import { SubscriptionModal } from "./components/SubscriptionModal";
import { AccountSettings } from "./components/AccountSettings";
import { WelcomeScreen, hasSeenWelcome } from "./components/WelcomeScreen";
import { SubscriptionPage } from "./components/SubscriptionPage";
import { AdminPanel } from "./components/AdminPanel";
import { DailyReport } from "./components/DailyReport";
import { VoiceSearchButton } from "./components/VoiceSearchButton";
import { MedicationTab } from "./MedicationTab";


const ScanTab = lazy(() => import("./tabs/ScanTab").then(m => ({ default: m.ScanTab })));
const WeightLossTab = lazy(() => import("./WeightLossTab").then(m => ({ default: m.WeightLossTab })));
import { NutritionComparePanel } from "./components/NutritionComparePanel";

import { UserDataProvider } from "./UserDataContext";

import type { Toast, AiRecipe, DietFilter, TabId, UserProfile } from "./types";
import { emojiUrl, fluentEmojiMap, preloadFoodImages } from "./utils/foodImages";
import { DIET_FILTERS } from "./data/dietFilters";
import { FilterMarquee } from "./components/FilterMarquee";
import { categoryIcons, ingredientCategories } from "./data/categories";
import { getDeviceId, isNetworkError } from "./utils/device";
import { normalizeName } from "./utils/imageHash";
import { getIngredientName, getRecipeName, getRecipeSteps, getOvenTemp, getCookingTips, getServingTips, scaleAmount } from "./utils/recipe";
import { checkCreditsForAction, getCreditsForEndpoint, useCredits } from "./hooks/useCredits";
import { validateRecipe } from "./utils/recipeValidation";
import { authBearerHeader } from "./utils/authGate";
import { useSubscription } from "./hooks/useSubscription";

function TabFallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-3 border-stone-300 dark:border-slate-600 border-t-blue-500 rounded-full animate-spin" />
    </div>
  );
}

// v2 — mobile redesign with warm colors + bottom navigation
export default function App() {
  const musicPlayer = useMusicPlayer();

  const [lang, setLang] = useState<Language>(() => {
    const stored = getStoredLang();
    if (stored) return stored;
    const deviceLang = (navigator.language || "ar").toLowerCase();
    return deviceLang.startsWith("en") ? "en" : "ar";
  });
  const [langPicked, setLangPicked] = useState(() => getStoredLang() !== null);
  const [isDark, setIsDark] = useState(() => localStorage.getItem("theme") !== "light");
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [activeTab, setActiveTab] = useState<TabId>("recipes");
  const [isWalking, setIsWalking] = useState(false);
  const [slideDir] = useState<"left" | "right" | null>(null);
  const slideWrapRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<FoodCategory>("all");
  const [activeRecipe, setActiveRecipe] = useState<Recipe | null>(null);
  const [activeServings, setActiveServings] = useState<number>(1);
  const [recipeHealthScore, setRecipeHealthScore] = useState<HealthScoreResult | null>(null);
  const [recipeHealthScoreLoading, setRecipeHealthScoreLoading] = useState(false);
  const [recipeDiarySaved, setRecipeDiarySaved] = useState<Set<string>>(new Set());
  const [showHelp, setShowHelp] = useState(false);
  const [showDietMenu, setShowDietMenu] = useState(false);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [toastCounter, setToastCounter] = useState(0);
  const [fridgeServings, setFridgeServings] = useState(2);
  const [fridgeExpanded, setFridgeExpanded] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [dishQuery, setDishQuery] = useState("");
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(() => window.location.hash.includes("type=recovery"));
  const [entryAuthMode] = useState<"signin" | "signup">(() => {
    try { return localStorage.getItem("last_email") ? "signin" : "signup"; } catch { return "signup"; }
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const { status: creditStatus, refresh: refreshCredits } = useCredits();
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showToolsMobile, setShowToolsMobile] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => !hasSeenWelcome());
  const [showSubPage, setShowSubPage] = useState(false);
  const { isActive: isSubscribed, isComplimentary } = useSubscription(session);
  const [consentAccepted, setConsentAccepted] = useState(() => {
    try { if (localStorage.getItem("hasAcceptedPrivacyPolicy") === "true") return true; } catch {}
    return getStoredConsentVersion() !== null;
  });
  const [, setDisclaimerAccepted] = useState(() => isDisclaimerAccepted());
  const [waterReminderEnabled, setWaterReminderEnabled] = useState(() => localStorage.getItem("water_reminder_enabled") !== "false");
  const [recipeImageOverrides, setRecipeImageOverrides] = useState<Record<string, string>>(() => {
    return cachedImageMap("recipe_img_cache", preservedImages.recipes);
  });
  const [ingredientImageOverrides, setIngredientImageOverrides] = useState<Record<string, string>>(() => {
    return cachedImageMap("ingredient_img_cache", preservedImages.ingredients);
  });
  const [, setCustomSplash] = useState<string | null>(null);
  const [customAppIcon, setCustomAppIcon] = useState<string | null>(preservedImages.settings.app_icon_url ? preservedImageUrl(preservedImages.settings.app_icon_url) : null);
  const [tabImages, setTabImages] = useState<Record<string, string>>(() => {
    return cachedImageMap("tab_btn_images", preservedImages.tabs);
  });
  const tabContentRef = useRef<HTMLDivElement>(null);
  const helpModalRef = useRef<HTMLDivElement>(null);
  const helpTriggerRef = useRef<HTMLElement | null>(null);
  const [trialUnlocked, setTrialUnlocked] = useState(() => {
    const info = getTrialInfo();
    if (info.permanent || info.active) return true;
    return false;
  });
  const [trialExpiredAlert, setTrialExpiredAlert] = useState(false);
  const [trialProgressPercent, setTrialProgressPercent] = useState(100);
  const [trialScansLeft, setTrialScansLeft] = useState<number>(() => {
    try {
      const raw = localStorage.getItem("scan_count");
      if (!raw) return 5;
      const data = JSON.parse(raw);
      const today = new Date().toISOString().slice(0, 10);
      if (data.date === today) return Math.max(0, 5 - data.count);
      return 5;
    } catch { return 5; }
  });
  const [trialTimeLeft, setTrialTimeLeft] = useState<string>("");
  const [dietFilter, setDietFilter] = useState<DietFilter | null>(null);
  const [fridgeAutoSearchDone, setFridgeAutoSearchDone] = useState(false);
  const [dishResult, setDishResult] = useState<
    | { type: "static"; recipes: (Recipe & { matchPct?: number })[] }
    | { type: "ai"; recipe: AiRecipe }
    | { type: "saved_ai"; recipes: AiRecipe[] }
    | { type: "loading" }
    | null
  >(null);
  const [searchHistory, setSearchHistory] = useState<{ id: string; query: string; result_type: string | null; created_at: string }[]>([]);
  const [searchMsg, setSearchMsg] = useState(0);

  useEffect(() => {
    if (dishResult?.type !== "loading") return;
    setSearchMsg(0);
    const t = setInterval(() => setSearchMsg(m => (m + 1) % 3), 1500);
    return () => clearInterval(t);
  }, [dishResult?.type]);

  useEffect(() => {
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  // Pre-cache all food emoji images for offline use
  useEffect(() => { preloadFoodImages(); }, []);

  // Auto-detect device language on first run
  useEffect(() => {
    if (getStoredLang()) return;
    const deviceLang = (navigator.language || "ar").toLowerCase();
    const detected: Language = deviceLang.startsWith("en") ? "en" : "ar";
    storeLang(detected);
    setLang(detected);
    setLangPicked(true);
  }, []);

  // Trial countdown ticker
  useEffect(() => {
    const raw = localStorage.getItem("trial_access");
    if (!raw) return;
    let data: any;
    try { data = JSON.parse(raw); } catch { return; }
    if (data.permanent) return;
    const end = new Date(data.startDate).getTime() + 3 * 24 * 60 * 60 * 1000;
    let expiredNotified = false;
    const tick = () => {
      const remaining = end - Date.now();
      if (remaining <= 0) {
        setTrialTimeLeft("");
        setTrialUnlocked(false);
        if (!expiredNotified) {
          expiredNotified = true;
          setTrialExpiredAlert(true);
        }
        return;
      }
      const d = Math.floor(remaining / (1000 * 60 * 60 * 24));
      const h = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const m = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((remaining % (1000 * 60)) / 1000);
      setTrialTimeLeft(`${d} ${t("trialDaysLeft")} - ${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`);
      const total = 3 * 24 * 60 * 60 * 1000;
      setTrialProgressPercent(Math.max(0, Math.min(100, (remaining / total) * 100)));
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [trialUnlocked, session]);

  // Help modal: focus trap + restoration
  useEffect(() => {
    if (!showHelp) return;
    helpTriggerRef.current = document.activeElement as HTMLElement;
    const modal = helpModalRef.current;
    if (!modal) return;
    const focusable = modal.querySelectorAll<HTMLElement>('button, a, input, [tabindex]:not([tabindex="-1"])');
    const first = focusable[0];
    if (first) first.focus();
    const trap = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || focusable.length === 0) return;
      const active = document.activeElement as HTMLElement;
      if (e.shiftKey) {
        if (active === first || !modal.contains(active)) { e.preventDefault(); focusable[focusable.length - 1].focus(); }
      } else {
        if (active === focusable[focusable.length - 1]) { e.preventDefault(); first.focus(); }
      }
    };
    modal.addEventListener("keydown", trap);
    return () => {
      modal.removeEventListener("keydown", trap);
      if (helpTriggerRef.current) helpTriggerRef.current.focus();
    };
  }, [showHelp]);

  // Update trial scans left when switching to scan tab or every 10s
  useEffect(() => {
    const refresh = () => {
      try {
        const raw = localStorage.getItem("scan_count");
        if (!raw) { setTrialScansLeft(5); return; }
        const data = JSON.parse(raw);
        const today = new Date().toISOString().slice(0, 10);
        if (data.date === today) setTrialScansLeft(Math.max(0, 5 - data.count));
        else setTrialScansLeft(5);
      } catch { setTrialScansLeft(5); }
    };
    refresh();
    const iv = setInterval(refresh, 10000);
    return () => clearInterval(iv);
  }, [activeTab]);

  useEffect(() => {
    const online = () => setIsOnline(true);
    const offline = () => setIsOnline(false);
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => { window.removeEventListener("online", online); window.removeEventListener("offline", offline); };
  }, []);

  const t = (key: string) => translations[lang][key] ?? key;
  const dir = languages.find(l => l.code === lang)?.dir ?? "rtl";

  const showToast = useCallback((message: string, type: "success" | "error" = "success") => {
    const id = toastCounter + 1;
    setToastCounter(id);
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  }, [toastCounter]);

  useEffect(() => {
    supabase.from("saved_recipes").select("recipe_id").eq("device_id", getDeviceId()).then(({ data }) => {
      if (data) setSavedIds(new Set(data.map(r => r.recipe_id)));
    });
    supabase.from("search_history").select("*").eq("device_id", getDeviceId()).order("created_at", { ascending: false }).limit(20).then(({ data }) => {
      if (data) setSearchHistory(data);
    });
    const deviceId = getDeviceId();
    supabase.from("user_profiles").select("*").eq("device_id", deviceId).maybeSingle().then(({ data }) => {
      if (data) setUserProfile(data as UserProfile);
    });
    let active = true;
    let receivedAuthEvent = false;
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!active || receivedAuthEvent) return;
      setSession(s);
      setAuthReady(true);
    }).catch(() => { if (active) setAuthReady(true); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!active) return;
      receivedAuthEvent = true;
      setAuthReady(true);
      if (_event === "PASSWORD_RECOVERY") setPasswordRecovery(true);
      (async () => {
        setSession(s);
        if (s) {
          try { localStorage.setItem("trial_access", JSON.stringify({ permanent: true, startDate: new Date().toISOString() })); } catch {}
          setTrialUnlocked(true);
          refreshCredits();
        } else {
          try {
            const raw = localStorage.getItem("trial_access");
            if (!raw) {
              localStorage.setItem("trial_access", JSON.stringify({ startDate: new Date().toISOString() }));
            }
          } catch {}
          setTrialUnlocked(true);
        }
      })();
    });
    supabase.from("recipe_images").select("recipe_id,image_url").then(({ data }) => {
      if (data) {
        const map: Record<string, string> = {};
        data.forEach((r: { recipe_id: string; image_url: string }) => { map[r.recipe_id] = preservedImageUrl(r.image_url); });
        setRecipeImageOverrides(map);
        try { localStorage.setItem("recipe_img_cache", JSON.stringify(map)); } catch {}
      }
    });
    supabase.from("ingredient_images").select("ingredient_id,image_url").then(({ data }) => {
      if (data) {
        const map: Record<string, string> = {};
        data.forEach((r: { ingredient_id: string; image_url: string }) => { map[r.ingredient_id] = preservedImageUrl(r.image_url); });
        setIngredientImageOverrides(map);
        try { localStorage.setItem("ingredient_img_cache", JSON.stringify(map)); } catch {}
      }
    });
    return () => { active = false; subscription.unsubscribe(); };
  }, []);

  // Sync consent to DB when session changes — never re-show the modal after first acceptance
  useEffect(() => {
    (async () => {
      // First-launch gate: if user already accepted, never re-show
      let alreadyAccepted = false;
      try { alreadyAccepted = localStorage.getItem("hasAcceptedPrivacyPolicy") === "true"; } catch {}
      if (alreadyAccepted) {
        // Sync to DB if logged in and not yet synced
        const stored = getStoredConsentVersion();
        if (stored && session?.user?.id) {
          const parts = stored.split("|");
          const termsV = parts.find(p => p.startsWith("terms:"))?.split(":")[1] ?? "1.0.0";
          const privacyV = parts.find(p => p.startsWith("privacy:"))?.split(":")[1] ?? "1.0.0";
          const guidelinesV = parts.find(p => p.startsWith("guidelines:"))?.split(":")[1] ?? "1.0.0";
          await supabase.from("user_consents").upsert({
            user_id: session.user.id,
            terms_version: termsV,
            privacy_version: privacyV,
            guidelines_version: guidelinesV,
            accepted_at: new Date().toISOString(),
          }, { onConflict: "user_id" });
          await supabase.from("user_profiles").update({
            consent_accepted: true,
            consent_version: stored,
            consent_date: new Date().toISOString(),
          }).eq("user_id", session.user.id);
        }
        return; // never re-show
      }

      const current = await checkConsentCurrent(session);
      if (current) return;

      // User already accepted locally before logging in — sync to DB silently
      const stored = getStoredConsentVersion();
      if (stored && session?.user?.id) {
        const parts = stored.split("|");
        const termsV = parts.find(p => p.startsWith("terms:"))?.split(":")[1] ?? "1.0.0";
        const privacyV = parts.find(p => p.startsWith("privacy:"))?.split(":")[1] ?? "1.0.0";
        const guidelinesV = parts.find(p => p.startsWith("guidelines:"))?.split(":")[1] ?? "1.0.0";
        await supabase.from("user_consents").upsert({
          user_id: session.user.id,
          terms_version: termsV,
          privacy_version: privacyV,
          guidelines_version: guidelinesV,
          accepted_at: new Date().toISOString(),
        }, { onConflict: "user_id" });
        await supabase.from("user_profiles").update({
          consent_accepted: true,
          consent_version: stored,
          consent_date: new Date().toISOString(),
        }).eq("user_id", session.user.id);
        return; // keep consentAccepted true — don't re-show
      }

      // No prior consent at all — show the screen
      clearStoredConsentVersion();
      setConsentAccepted(false);
    })();
  }, [session]);

  // Load tab button images from Supabase (card_images)
  useEffect(() => {
    supabase.from('card_images').select('tab,image_url').then(({ data }) => {
      if (!data) return;
      const map: Record<string, string> = {};
      if (data) {
        data.forEach((r: { tab: string; image_url: string }) => { map[r.tab] = preservedImageUrl(r.image_url); });
      }
      try { localStorage.setItem('tab_btn_images', JSON.stringify(map)); } catch {}
      setTabImages(map);
    });
  }, []);

  // Load custom app images (splash + icon)
  useEffect(() => {
    supabase.from("app_settings").select("splash_image_url,app_icon_url").eq("id", 1).maybeSingle().then(({ data }) => {
      if (data) {
        if (data.splash_image_url) setCustomSplash(preservedImageUrl(data.splash_image_url));
        if (data.app_icon_url) setCustomAppIcon(preservedImageUrl(data.app_icon_url));
      }
    });
  }, []);

  // Dynamically update favicon and manifest icon
  useEffect(() => {
    if (customAppIcon) {
      const favicons = document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]');
      favicons.forEach(f => { f.href = customAppIcon; });
      const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
      if (manifestLink) {
        fetch(manifestLink.href)
          .then(r => r.json())
          .then(m => {
            m.icons?.forEach((i: any) => { i.src = customAppIcon; });
            const blob = new Blob([JSON.stringify(m)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            manifestLink.href = url;
          })
          .catch(() => {});
      }
    }
  }, [customAppIcon]);

  // Dynamically update document title based on selected language
  useEffect(() => {
    document.title = t("appTitle");
  }, [lang]);

  useEffect(() => {
    if (activeRecipe) setActiveServings(activeRecipe.servings);
  }, [activeRecipe?.id]);

  const toggleSave = async (recipeId: string) => {
    const deviceId = getDeviceId();
    if (savedIds.has(recipeId)) {
      const { error } = await supabase.from("saved_recipes").delete().eq("recipe_id", recipeId).eq("device_id", deviceId);
      if (!error) {
        setSavedIds(prev => { const n = new Set(prev); n.delete(recipeId); return n; });
        showToast(t("removeFromSaved"));
      }
    } else {
      const { error } = await supabase.from("saved_recipes").insert({ recipe_id: recipeId, device_id: deviceId });
      if (!error) {
        setSavedIds(prev => new Set([...prev, recipeId]));
        showToast(t("savedRecipe"));
      }
    }
  };

  const shareRecipe = async (recipe: Recipe) => {
    const name = getRecipeName(recipe, lang);
    const steps = getRecipeSteps(recipe, lang);
    const stepsText = steps.length > 0
      ? (lang === "ar" ? `\n\n${t("steps")}:\n` : `\n\n${t("steps")}:\n`) + steps.map((s, i) => `${i + 1}. ${s}`).join("\n")
      : "";
    const text = `${t("appTitle")}\npurelifesmart.com\n${t("steps") === "طريقة التحضير" ? "اسم الطبق" : "Recipe"}: ${name}${stepsText}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${t("appTitle")} - ${name}`, text });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        showToast(t("shareCopied"));
      } else {
        showToast(text, "success");
      }
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        try { await navigator.clipboard.writeText(text); showToast(t("shareCopied")); } catch { /* ignore */ }
      }
    }
  };

  const autoSave = async (recipeId: string, recipeName: string) => {
    if (savedIds.has(recipeId)) return;
    const { error } = await supabase.from("saved_recipes").insert({ recipe_id: recipeId, recipe_name: recipeName, device_id: getDeviceId() });
    if (!error) setSavedIds(prev => new Set([...prev, recipeId]));
  };

  const [healthScoreFetching, setHealthScoreFetching] = useState(false);

  const openRecipe = (recipe: Recipe) => {
    const override = recipeImageOverrides[recipe.id];
    setActiveRecipe(override ? { ...recipe, image: override } : recipe);
    autoSave(recipe.id, recipe.nameEn ?? recipe.nameAr ?? recipe.id);
    setRecipeHealthScore(null);
    setRecipeHealthScoreLoading(false);
  };

  const fetchRecipeHealthScore = async (recipe: Recipe) => {
    if (healthScoreFetching) return;
    const recipeName = recipe.nameAr || recipe.nameEn || recipe.id;
    setHealthScoreFetching(true);
    setRecipeHealthScore(null);
    setRecipeHealthScoreLoading(true);
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setRecipeHealthScoreLoading(false); return; }
      const creditCheck = await checkCreditsForAction(getCreditsForEndpoint("health-score"));
      if (!creditCheck.allowed) {
        showToast(creditCheck.reason === "unavailable" ? t("creditUnavailable") : t("creditInsufficient"), "error");
        setRecipeHealthScoreLoading(false); return;
      }
      const res = await fetch(`${supabaseUrl}/functions/v1/health-score`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: authBearerHeader(session), Apikey: supabaseAnonKey },
        body: JSON.stringify({ foodText: recipeName, lang, userGoal: userProfile?.goal ?? "healthy" }),
      });
      if (res.ok) {
        const data = await res.json() as HealthScoreResult;
        applyCanonicalNutrition(data, recipe.nutrition);
        setRecipeHealthScore(data);
      } else if (res.status === 402) {
        showToast(t("creditInsufficient"), "error");
        setRecipeHealthScoreLoading(false); return;
      }
    } catch (err) {
      if (isNetworkError(err)) showToast(t("networkError"), "error");
      setRecipeHealthScoreLoading(false);
    } finally {
      try { await refreshCredits(); } catch { /* refresh failure must not mask primary result */ }
      setHealthScoreFetching(false);
      setRecipeHealthScoreLoading(false);
    }
  };

  const saveFridgeRecipeToDiary = async (recipe: Recipe, score: HealthScoreResult | null) => {
    if (recipeDiarySaved.has(recipe.id)) return;
    const recipeName = getRecipeName(recipe, lang);
    const { data } = await supabase.from("food_scans").insert({
      device_id: getDeviceId(),
      dish_name: recipeName,
      cuisine: null,
      recipe_data: null,
      calories: recipe.nutrition?.calories ?? null,
      image_url: recipe.image || null,
      health_score: score ?? null,
    }).select().single();
    if (data) setRecipeDiarySaved(prev => new Set([...prev, recipe.id]));
  };

  const [searchDishFetching, setSearchDishFetching] = useState(false);

  const searchDish = async (queryOverride?: string) => {
    const query = (queryOverride !== undefined ? queryOverride : dishQuery).trim();
    if (!query) return;
    if (searchDishFetching) return;
    const q = query.toLowerCase();

    const found = recipes.filter(r =>
      [r.nameAr, r.nameEn]
        .some(name => name?.toLowerCase().includes(q))
    );

    if (found.length > 0) {
      setDishResult({ type: "static", recipes: found });
      found.forEach(r => autoSave(r.id, r.nameEn ?? r.nameAr ?? r.id));
      supabase.from("search_history").insert({ query, result_type: "static", device_id: getDeviceId() }).then(({ data }) => {
        if (data) setSearchHistory(prev => [data[0], ...prev.slice(0, 19)]);
      });
      supabase.from("search_history").select("*").eq("device_id", getDeviceId()).order("created_at", { ascending: false }).limit(20).then(({ data }) => {
        if (data) setSearchHistory(data);
      });
      return;
    }

    setDishResult({ type: "loading" });
    setSearchDishFetching(true);
    const recordHistory = (resultType: string) => {
      supabase.from("search_history").insert({ query, result_type: resultType, device_id: getDeviceId() }).then(() => {
        supabase.from("search_history").select("*").eq("device_id", getDeviceId()).order("created_at", { ascending: false }).limit(20).then(({ data: h }) => {
          if (h) setSearchHistory(h);
        });
      });
    };

    try {
      // Generated results are cached by the server using the complete request.
      const { data: { session: sbSession } } = await supabase.auth.getSession();
      if (!sbSession) { showToast(t("aiSignInRequired"), "error"); setDishResult(null); return; }
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/generate-recipe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${sbSession?.access_token ?? supabaseAnonKey}`,
          "Apikey": supabaseAnonKey,
        },
        body: JSON.stringify({ dishName: query, lang, servings: fridgeServings, dietFilter, requestId: crypto.randomUUID() }),
      });
      const data = await res.json();
      if (res.status === 402) { showToast(t("creditInsufficient"), "error"); setDishResult(null); return; }
      if (!res.ok || data.error) throw new Error(data.error ?? "Failed");
      const validation = validateRecipe(data);
      if (!validation.valid || !validation.recipe) {
        throw new Error(validation.errors.join("; "));
      }
      const aiRecipe = validation.recipe;
      setDishResult({ type: "ai", recipe: aiRecipe });
      recordHistory("ai");
      refreshCredits();
      supabase.from("saved_ai_recipes").upsert(
        { name: aiRecipe.name, query: normalizeName(query), recipe_data: aiRecipe, lang },
        { onConflict: "name,lang", ignoreDuplicates: true }
      );
    } catch (err) {
      showToast(isNetworkError(err) ? t("networkError") : t("aiError"), "error");
      setDishResult(null);
    } finally {
      try { await refreshCredits(); } catch { /* refresh failure must not mask primary result */ }
      setSearchDishFetching(false);
    }
  };

  const toggle = (id: string) => {
    setFridgeAutoSearchDone(false);
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const suggestedRecipes = useMemo(() => {
    if (selected.size === 0) return [];
    const selectedArr = Array.from(selected);
    return recipes
      .filter(r => r.category !== "athletes" && r.category !== "pastries" && r.category !== "desserts")
      .filter((r, i, arr) => arr.findIndex(x => x.id === r.id) === i)
      .map(r => {
        const allSelectedInRecipe = selectedArr.every(id =>
          r.requiredIngredients.includes(id) || r.optionalIngredients.includes(id)
        );
        const coveredRequired = r.requiredIngredients.filter(i => selected.has(i)).length;
        const pct = Math.round((coveredRequired / r.requiredIngredients.length) * 100);
        return { ...r, matchPct: pct, allSelectedPresent: allSelectedInRecipe };
      })
      .filter(r => r.allSelectedPresent)
      .sort((a, b) => b.matchPct - a.matchPct);
  }, [selected]);

  const menuRecipes = useMemo(() => {
    let list = recipes;
    if (selectedCategory === "all") {
      list = list.filter(r => r.category !== "athletes" && r.category !== "pastries" && r.category !== "desserts");
    } else if (selectedCategory === "desserts") {
      list = list.filter(r => r.category === "desserts" || r.category === "pastries");
    } else {
      list = list.filter(r => r.category === selectedCategory);
    }
    if (dietFilter) {
      const activeDiet = DIET_FILTERS.find(f => f.id === dietFilter);
      if (activeDiet) list = list.filter(r => activeDiet.passes(r));
    }
    const seen = new Set<string>();
    list = list.filter(r => { if (seen.has(r.id)) return false; seen.add(r.id); return true; });
    return list;
  }, [selectedCategory, lang, dietFilter]);

  useEffect(() => {
    if (activeTab !== "recipes") return;
    if (selected.size === 0) return;
    if (dishResult !== null) return;
    if (fridgeAutoSearchDone) return;
    if (suggestedRecipes.length > 0) return;
    const ingredientNames = [...selected]
      .map(id => fridgeIngredients.find(i => i.id === id))
      .filter(Boolean)
      .map(i => getIngredientName(i!, lang))
      .slice(0, 6)
      .join(", ");
    if (!ingredientNames) return;
    const timer = setTimeout(() => {
      setDishQuery(ingredientNames);
      setFridgeAutoSearchDone(true);
      searchDish(ingredientNames);
    }, 800);
    return () => clearTimeout(timer);
  }, [activeTab, selected.size, suggestedRecipes.length, dishResult, fridgeAutoSearchDone, lang]);

  const [fridgeSearch, setFridgeSearch] = useState("");
  const [fridgeInputVal, setFridgeInputVal] = useState("");

  const normalizeAr = (s: string) =>
    s.replace(/[أإآٱ]/g, "ا").replace(/ة/g, "ه").replace(/ى/g, "ي")
     .replace(/[\u064B-\u065F\u0670]/g, "").toLowerCase().trim();

  const groupedIngredients = useMemo(() => {
    const q = normalizeAr(fridgeSearch);
    const qEn = fridgeSearch.trim().toLowerCase();
    return Object.entries(ingredientCategories).map(([key, cat]) => ({
      key, cat,
      items: fridgeIngredients.filter(i => i.category === key && (
        !q || normalizeAr(i.nameAr).includes(q) || i.nameEn.toLowerCase().includes(qEn)
      )),
    })).filter(g => g.items.length > 0);
  }, [fridgeSearch]);

  const foodCategories: FoodCategory[] = ["all", "salads", "breakfast", "grilled", "seafood", "desserts", "juices", "athletes", "soups"];

  if (!authReady) {
    return <div className="min-h-[100dvh] flex items-center justify-center bg-[#0A2240] text-white" dir={dir} role="status">
      {dir === "rtl" ? "جارٍ التحميل…" : "Loading…"}
    </div>;
  }

  if (!session || passwordRecovery) {
    return <div className="relative">
      <div className="absolute top-4 inset-x-4 z-10 flex justify-end gap-2" aria-label="Language / اللغة">
        {(["ar", "en"] as const).map(code => <button key={code} type="button"
          onClick={() => { setLang(code); storeLang(code); setLangPicked(true); }}
          aria-pressed={lang === code}
          className={`px-4 py-2 rounded-xl text-sm ${lang === code ? "bg-white text-[#0A2240]" : "bg-white/10 text-white"}`}>
          {code === "ar" ? "العربية" : "English"}
        </button>)}
      </div>
      <AuthModal open={true} session={session} onClose={() => {}} t={t} dir={dir}
        initialMode={entryAuthMode} required page recovery={passwordRecovery}
        onRecoveryComplete={() => setPasswordRecovery(false)}
        onSuccess={setSession} onProfileSaved={p => setUserProfile(p)} />
    </div>;
  }

  return (
    <>
    {/* After account sign-in, preserve the welcome and consent steps. */}
    {showWelcome && (
      <WelcomeScreen t={t} dir={dir} onContinue={() => setShowWelcome(false)} />
    )}
    {/* Step 2: Consent gate */}
    {langPicked && !showWelcome && !consentAccepted && (
      <ConsentGate
        t={t}
        dir={dir}
        session={session}
        onAccept={() => { try { localStorage.setItem("hasAcceptedPrivacyPolicy", "true"); } catch {} setConsentAccepted(true); setDisclaimerAccepted(true); setTrialUnlocked(true); }}
        onDecline={() => { try { localStorage.removeItem("hasAcceptedPrivacyPolicy"); } catch {} clearStoredConsentVersion(); window.location.reload(); }}
      />
    )}

    {/* Trial expired alert popup */}
    {trialExpiredAlert && (
      <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" dir={dir}>
        <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-red-500/30 overflow-hidden animate-in fade-in zoom-in-95">
          <div className="bg-gradient-to-br from-red-600 to-orange-600 px-6 py-8 flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-bold text-xl text-white">{t("trialExpired")}</h1>
            <p className="text-sm text-white/80 text-center leading-relaxed">{t("trialSignInSubtitle")}</p>
          </div>
          <div className="px-6 py-6 space-y-3">
            <button
              onClick={() => { setTrialExpiredAlert(false); setShowAuthModal(true); }}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2"
            >
              {t("signIn")}
            </button>
            <button
              onClick={() => { setTrialExpiredAlert(false); setShowSubModal(true); }}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2"
            >
              {t("trialSubscribeBtn")}
            </button>
            <button
              onClick={() => setTrialExpiredAlert(false)}
              className="w-full py-3 rounded-xl border-2 border-stone-200 dark:border-slate-700 text-stone-600 dark:text-slate-300 font-medium text-sm transition-all hover:bg-stone-50 dark:hover:bg-slate-800"
            >
              {t("close")}
            </button>
          </div>
        </div>
      </div>
    )}
    <div className={`min-h-screen font-sans bg-slate-50 dark:bg-slate-900 transition-colors${isDark ? " dark" : ""}`} dir={dir}>



      {/* Toast notifications */}
      <div className="fixed top-4 start-1/2 z-[100] flex flex-col gap-2 pointer-events-none" style={{ transform: "translateX(-50%)" }}>
        {toasts.map(toast => (
          <div key={toast.id} className={`px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium animate-in fade-in slide-in-from-top-2 ${toast.type === "success" ? "bg-blue-600" : "bg-red-600"}`}>
            {toast.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="bg-[#0A2240] dark:bg-[#050E1F] z-40" dir={dir}>
        <div className="w-full py-3 px-4 bg-[#0A2240] dark:bg-[#050E1F] text-center rounded-b-xl mb-3 border-b border-blue-800/40">
          <div className="flex items-center justify-center gap-2">
            <img src="/brand-mark.svg" alt={t("bannerSubtitle")} className="w-6 h-6 md:w-7 md:h-7 shrink-0" />
            <h1 className="text-lg md:text-xl font-semibold text-white">
              {t("bannerSubtitle")}
            </h1>
          </div>
        </div>
        {/* Trial countdown reminder — visible whenever user is on a non-permanent trial */}
        {trialUnlocked && !session && (() => { const ti = getTrialInfo(); return ti.active; })() && (
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white">
            <div className="px-4 py-2.5 flex items-center justify-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center animate-pulse shrink-0">
                  <Clock className="w-4 h-4" />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-[9px] font-semibold uppercase tracking-wide text-white/70">{t("trialCountdown")}</span>
                  <span className="text-xs font-bold tabular-nums">{trialTimeLeft || "3 " + t("trialDaysLeft")}</span>
                </div>
              </div>
              <div className="w-px h-7 bg-white/30" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <Camera className="w-4 h-4" />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-[9px] font-semibold uppercase tracking-wide text-white/70">{t("trialScansLeft")}</span>
                  <span className="text-xs font-bold">{trialScansLeft}</span>
                </div>
              </div>
            </div>
            <div className="h-1 bg-black/20">
              <div className="h-full bg-white/90 transition-all duration-1000" style={{ width: `${trialProgressPercent}%` }} />
            </div>
          </div>
        )}
        {!isOnline && (
          <div className="bg-amber-500 text-amber-950 text-center text-sm font-bold py-2 px-4 flex items-center justify-center gap-2">
            <WifiOff className="w-4 h-4 shrink-0" />
            {t("networkOfflineBanner")} — {t("networkError")}
          </div>
        )}
      </header>

      {showHelp && <div className="fixed inset-0 z-30" onClick={() => setShowHelp(false)} />}

      {/* Help modal */}
      {showHelp && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowHelp(false)} onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); setShowHelp(false); } }}>
          <div ref={helpModalRef} className="bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col" dir={dir} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="help-modal-title">
            {/* Header */}
            <div className="bg-gradient-to-br from-[#0A2240] to-[#0E3360] px-5 pt-5 pb-4 rounded-t-3xl shrink-0">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center">
                    <Info className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 id="help-modal-title" className="text-white font-bold text-base leading-tight">{t("aboutPurelife")}</h2>
                    <p className="text-[11px] font-semibold" style={{ color: "#fde68a" }}>{t("appTitle")}</p>
                  </div>
                </div>
                <button onClick={() => setShowHelp(false)} aria-label={t("closeDialog")} className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors focus:ring-2 focus:ring-white/50">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-white/75 text-xs leading-relaxed">{t("aboutDesc")}</p>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto flex-1 px-4 py-4 space-y-2">
              <p className="text-[11px] font-bold uppercase tracking-widest text-stone-400 dark:text-slate-500 px-1 mb-3">{t("helpTitle")}</p>
              {([
                { icon: Refrigerator, color: "bg-sky-50 dark:bg-sky-500/10 border-sky-200 dark:border-sky-500/30",           iconColor: "text-sky-600",      labelKey: "helpFridgeLabel",           descKey: "helpFridgeDesc" },
                { icon: Camera,       color: "bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30",        iconColor: "text-blue-600",     labelKey: "helpScanLabel",            descKey: "helpScanDesc" },
                { icon: Barcode,      color: "bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/30",iconColor: "text-indigo-600",   labelKey: "helpBarcodeLabel",         descKey: "helpBarcodeDesc" },
                { icon: Search,       color: "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30",    iconColor: "text-amber-600",    labelKey: "helpRecipesLabel",         descKey: "helpRecipesDesc" },
                { icon: BookOpen,     color: "bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/30",iconColor: "text-orange-600",   labelKey: "helpMenuLabel",            descKey: "helpMenuDesc" },
                { icon: Bookmark,     color: "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30",        iconColor: "text-rose-600",     labelKey: "helpSavedLabel",           descKey: "helpSavedDesc" },
                { icon: Scale,        color: "bg-teal-50 dark:bg-teal-500/10 border-teal-200 dark:border-teal-500/30",        iconColor: "text-teal-600",     labelKey: "helpWLLabel",              descKey: "helpWLDesc" },
                { icon: Footprints,   color: "bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30",    iconColor: "text-green-600",    labelKey: "helpStepsLabel",           descKey: "helpStepsDesc" },

                { icon: Award,        color: "bg-yellow-50 dark:bg-yellow-500/10 border-yellow-200 dark:border-yellow-500/30",iconColor: "text-yellow-600",   labelKey: "helpHealthScoreLabel",     descKey: "helpHealthScoreDesc" },
                { icon: ShieldAlert,  color: "bg-fuchsia-50 dark:bg-fuchsia-500/10 border-fuchsia-200 dark:border-fuchsia-500/30", iconColor: "text-fuchsia-600", labelKey: "helpAllergyLabel",        descKey: "helpAllergyDesc" },
                { icon: Droplets,     color: "bg-cyan-50 dark:bg-cyan-500/10 border-cyan-200 dark:border-cyan-500/30",        iconColor: "text-cyan-600",     labelKey: "helpWaterLabel",           descKey: "helpWaterDesc" },
                { icon: PieChart,     color: "bg-sky-50 dark:bg-sky-500/10 border-sky-200 dark:border-sky-500/30",            iconColor: "text-sky-500",      labelKey: "helpNutritionLabel",       descKey: "helpNutritionDesc" },
                { icon: FlameKindling,color: "bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/30",iconColor: "text-orange-500",   labelKey: "helpCalorieCompareLabel",  descKey: "helpCalorieCompareDesc" },
                { icon: Music2,       color: "bg-violet-50 dark:bg-violet-500/10 border-violet-200 dark:border-violet-500/30",iconColor: "text-violet-600",   labelKey: "helpMusicLabel",           descKey: "helpMusicDesc" },
              ] as const).map(({ icon: Icon, color, iconColor, labelKey, descKey }, i) => (
                <div key={i} className={`flex items-start gap-3 rounded-2xl border p-3.5 transition-all duration-200 hover:scale-[1.02] active:scale-[0.97] ${color}`}>
                  <div className="rounded-xl p-2 bg-white/70 dark:bg-white/10 shrink-0 mt-0.5">
                    <Icon className={`w-4 h-4 ${iconColor}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-stone-700 dark:text-slate-200 mb-0.5">{t(labelKey)}</p>
                    <p className="text-stone-600 dark:text-slate-400 text-xs leading-relaxed">{t(descKey)}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-4 pb-5 pt-2 shrink-0 border-t border-stone-100 dark:border-slate-800 space-y-2">
              <div className="flex flex-col items-center gap-1.5 py-2">
                <a href="https://www.purelifesmart.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                  <Link2 className="w-3.5 h-3.5" />
                  www.purelifesmart.com
                </a>
                <a href="mailto:info@purelifesmart.com" className="flex items-center gap-2 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                  <Mail className="w-3.5 h-3.5" />
                  info@purelifesmart.com
                </a>
              </div>
              <p className="text-[10px] leading-relaxed text-stone-400 dark:text-slate-500 text-center px-2">{t("medicalDisclaimer")}</p>
              <button onClick={() => setShowHelp(false)} className="w-full bg-[#0A2240] hover:bg-[#071832] text-[#F5D98A] font-bold py-3.5 rounded-2xl transition-all shadow hover:shadow-md text-sm focus:ring-2 focus:ring-blue-400/50">
                {t("helpBtn")} ✓
              </button>
            </div>
          </div>
        </div>
      )}

      <AuthModal open={showAuthModal} session={session} onClose={() => setShowAuthModal(false)} t={t} dir={dir} onSuccess={setSession} onProfileSaved={p => setUserProfile(p)} onSignOut={() => { supabase.auth.signOut({ scope: "local" }); try { localStorage.removeItem("last_email"); } catch {} setShowAuthModal(false); }} />
      <SubscriptionModal open={showSubModal} session={session} complimentary={isComplimentary} onClose={() => setShowSubModal(false)} onSuccess={() => { setShowSubModal(false); }} t={t} dir={dir} lang={lang} />
      <AccountSettings open={showAccountSettings} session={session} userProfile={userProfile} onClose={() => setShowAccountSettings(false)} onProfileSaved={p => setUserProfile(p)} onSignOut={() => { supabase.auth.signOut({ scope: "local" }); try { localStorage.removeItem("last_email"); } catch {} setShowAccountSettings(false); }} t={t} dir={dir} />
      <AdminPanel open={showAdminPanel} session={session} onClose={() => setShowAdminPanel(false)} t={t} dir={dir} />
      {showSubPage && (
        <SubscriptionPage t={t} dir={dir} lang={lang} session={session} onSelectPlan={() => { setShowSubPage(false); setShowAuthModal(true); }} onContinueFree={() => setShowSubPage(false)} onBack={() => setShowSubPage(false)} />
      )}

      {/* Diet filter menu */}
      {showDietMenu && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowDietMenu(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-t-3xl shadow-2xl w-full max-w-lg" dir={dir} onClick={e => e.stopPropagation()}>
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-stone-200 dark:bg-slate-600" />
            </div>
            <div className="px-5 pt-3 pb-4 flex items-center justify-between border-b border-stone-100 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-600" />
                <h2 className="font-bold text-stone-800 dark:text-slate-100 text-base">{t("selectCategory")}</h2>
                {suggestedRecipes.length > 0 && (
                  <span className="text-xs font-semibold bg-blue-100 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full">{suggestedRecipes.length}</span>
                )}
              </div>
              <button onClick={() => setShowDietMenu(false)} className="w-8 h-8 rounded-full bg-stone-100 dark:bg-slate-700 flex items-center justify-center text-stone-500 dark:text-slate-400 hover:bg-stone-200 dark:hover:bg-slate-600 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-xs text-stone-400 dark:text-slate-500 mb-3">{t("optional")}</p>
              <div className="flex flex-wrap gap-2">
                {DIET_FILTERS.map(f => (
                  <button key={f.id} onClick={() => setDietFilter(prev => prev === f.id ? null : f.id)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border-2 transition-all ${dietFilter === f.id ? "bg-blue-600 border-blue-600 text-white shadow-md scale-105" : "bg-stone-50 dark:bg-slate-700/50 border-stone-200 dark:border-slate-600 text-stone-600 dark:text-slate-300 hover:border-blue-300 dark:hover:border-blue-600/50"}`}
                  >
                    <span>{f.icon}</span>
                    {f.label[lang]}
                    {dietFilter === f.id && <Check className="w-3.5 h-3.5 ms-0.5" />}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-5 pb-6 pt-2 flex gap-3">
              {dietFilter && (
                <button onClick={() => setDietFilter(null)} className="flex-none px-4 py-3.5 rounded-2xl border-2 border-stone-200 dark:border-slate-600 text-stone-600 dark:text-slate-300 text-sm font-medium hover:bg-stone-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2">
                  <X className="w-4 h-4" />{t("clearAll")}
                </button>
              )}
              <button onClick={() => { setShowDietMenu(false); setActiveTab("recipes"); }}
                className="flex-1 py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white font-bold text-base shadow-lg hover:shadow-blue-600/25 transition-all flex items-center justify-center gap-2">
                <Search className="w-5 h-5" />
                {t("findRecipes")}
                {dietFilter && <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">1</span>}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-4 sm:pb-32 relative">
        <div ref={slideWrapRef} className={`${slideDir === "left" ? "animate-slide-left" : slideDir === "right" ? "animate-slide-right" : ""}`}>

        <UserDataProvider session={session}>

        {/* Utility bar — collapsible in-flow on mobile, fixed on desktop */}
        <div className={activeTab === "meds" ? "mb-3" : "sm:fixed sm:bottom-0 sm:left-0 sm:right-0 sm:z-40 mb-3 sm:mb-0"} dir={dir}>
          <div className="mx-auto max-w-md sm:max-w-lg md:max-w-2xl lg:max-w-4xl xl:max-w-6xl px-0 sm:px-2 sm:pb-2" style={{ paddingBottom: "max(0px, env(safe-area-inset-bottom))" }}>
            <div className="rounded-2xl border border-stone-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-md overflow-hidden">
              <button
                onClick={() => setShowToolsMobile(v => !v)}
                className="sm:hidden flex items-center justify-between w-full px-3 py-2.5 text-stone-700 dark:text-slate-200"
                aria-expanded={showToolsMobile}
                aria-controls="utility-panel"
              >
                <span className="text-sm font-medium">{lang === "ar" ? "أدوات الحساب" : "Account tools"}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showToolsMobile ? "rotate-180" : ""}`} />
              </button>
              <div id="utility-panel" className={`${showToolsMobile ? "block" : "hidden"} sm:block`}>
                <div className="grid grid-cols-2 gap-1.5 p-2 sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:gap-2">
                  <button onClick={() => setIsDark(d => !d)} className="flex items-center gap-2.5 px-3 h-11 rounded-xl hover:bg-stone-100 dark:hover:bg-slate-700 transition-colors" title={isDark ? t("lightMode") : t("darkMode")}>
                    {isDark ? <Sun className="w-5 h-5 text-amber-500" /> : <Moon className="w-5 h-5 text-blue-600" />}
                    <span className="text-sm font-medium text-stone-700 dark:text-slate-200">{isDark ? t("lightMode") : t("darkMode")}</span>
                  </button>
                  <button onClick={() => setLang(lang === "ar" ? "en" : "ar")} className="flex items-center gap-2.5 px-3 h-11 rounded-xl hover:bg-stone-100 dark:hover:bg-slate-700 transition-colors" title={t("language")}>
                    <Globe className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-stone-700 dark:text-slate-200">{languages.find(l => l.code === lang)?.label}</span>
                  </button>
                  <WaterReminder lang={lang} onToast={showToast} reminderEnabled={waterReminderEnabled} onToggleReminder={() => setWaterReminderEnabled(v => { const nv = !v; localStorage.setItem("water_reminder_enabled", String(nv)); showToast(nv ? t("waterReminderEnabled") : t("waterReminderDisabled")); return nv; })} />
                  <button onClick={() => setShowHelp(true)} className="flex items-center gap-2.5 px-3 h-11 rounded-xl hover:bg-stone-100 dark:hover:bg-slate-700 transition-colors" title={t("helpBtn")}>
                    <HelpCircle className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-stone-700 dark:text-slate-200">{t("helpBtn")}</span>
                  </button>
                  <button onClick={() => session ? setShowAccountSettings(true) : setShowAuthModal(true)} className="flex items-center gap-2.5 px-3 h-11 rounded-xl hover:bg-stone-100 dark:hover:bg-slate-700 transition-colors min-w-0" title={session ? session.user.email ?? "" : t("signIn")}>
                    <User className="w-5 h-5 text-blue-600 shrink-0" />
                    <span className="text-sm font-medium text-stone-700 dark:text-slate-200 truncate">
                      {session ? (<>{lang === "ar" ? "الحساب" : "Account"} <span dir="ltr" className="opacity-70">{(session.user.email ?? "").split("@")[0]}</span></>) : t("signIn")}
                    </span>
                  </button>
                  <button onClick={() => setShowSubModal(true)} className="flex items-center gap-2.5 px-3 h-11 rounded-xl hover:bg-stone-100 dark:hover:bg-slate-700 transition-colors" title={isComplimentary ? (lang === "ar" ? "وصول خاص دائم" : "Lifetime complimentary access") : t("subUpgradeBtn")}>
                    <Crown className="w-5 h-5 text-amber-500" />
                    <span className="text-sm font-medium text-stone-700 dark:text-slate-200">{isComplimentary ? (lang === "ar" ? "وصول خاص دائم" : "Lifetime complimentary access") : t("subUpgradeBtn")}</span>
                  </button>
                  {session && creditStatus.loaded && (
                    creditStatus.error ? (
                      <div className="col-span-2 sm:col-span-1 flex items-center justify-between px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20">
                        <span className="text-sm text-amber-700 dark:text-amber-300">{t("creditStatusUnavailable")}</span>
                        <button onClick={refreshCredits} className="text-sm font-medium text-amber-600 dark:text-amber-400 hover:underline">
                          {t("creditRetry")}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2.5 px-3 h-11 rounded-xl" title={creditStatus.tier === "free" ? `${creditStatus.remaining_lifetime} ${t("creditsRemaining")}` : `${creditStatus.remaining_month}/${creditStatus.month_limit} ${t("creditsRemaining")}`}>
                        <Sparkles className="w-5 h-5 text-amber-500 shrink-0" />
                        <span className="text-sm font-medium text-stone-700 dark:text-slate-200">
                          {creditStatus.tier === "free" ? `${creditStatus.remaining_lifetime} ${t("creditsRemaining")}` : `${creditStatus.remaining_month}/${creditStatus.month_limit} ${t("creditsRemaining")}`}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section navigation — compact tiles */}
        <div className="mb-3">
          <div className="grid grid-cols-2 gap-2.5 max-w-md mx-auto w-full sm:grid-cols-3 sm:max-w-none lg:grid-cols-4 xl:grid-cols-7 sm:gap-3" dir={dir}>
            {([
              { tab: "recipes" as const, Icon: Search },
              { tab: "fridge" as const, Icon: Refrigerator },
              { tab: "scan" as const, Icon: Camera },
              { tab: "weight" as const, Icon: TrendingDown },
              { tab: "steps" as const, Icon: Footprints },
              { tab: "report" as const, Icon: Award },
              { tab: "meds" as const, Icon: Pill },
            ]).map(({ tab, Icon }) => {
              const isActive = activeTab === tab;
              const userImg = tabImages[`${tab}_${lang}`] ?? tabImages[tab] ?? null;
              const tabImg = userImg;
              return (
                <div key={tab} className={`relative ${tab === "meds" ? "col-span-2 sm:col-span-1" : ""}`}>
                  <div onClick={() => {
                    const wasActive = activeTab === tab;
                    if (tab === "fridge") {
                      if (wasActive) setFridgeExpanded(v => !v);
                      else { setActiveTab("fridge"); setFridgeExpanded(true); }
                    } else { setActiveTab(tab); }
                    setTimeout(() => tabContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
                  }}
                    className={`w-full flex items-center gap-2.5 px-3 min-h-[64px] rounded-xl border transition-all duration-200 overflow-hidden relative cursor-pointer
                      ${isActive
                        ? "border-blue-600 bg-blue-600"
                        : "border-stone-200 dark:border-slate-700 bg-stone-50 dark:bg-slate-800 hover:border-blue-300 dark:hover:border-blue-600/50"
                      }`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.currentTarget.click(); } }}
                  >
                    {tabImg && (
                      <img src={tabImg} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none opacity-30" />
                    )}
                    <div className="relative z-10 flex items-center gap-2.5 min-w-0 w-full">
                      <Icon className={`w-5 h-5 shrink-0 ${isActive ? "text-white" : "text-stone-500 dark:text-slate-400"}`} />
                      <div className="flex flex-col min-w-0">
                        <span className={`text-sm font-medium leading-tight truncate ${isActive ? "text-white" : "text-stone-700 dark:text-slate-200"}`}>
                          {t(`${tab}Tab`)}
                          {tab === "fridge" && selected.size > 0 && (
                            <span className={`ms-1.5 inline-flex items-center justify-center min-w-[18px] h-4 ${isActive ? "bg-white/25" : "bg-blue-600"} text-white text-[10px] font-bold rounded-full px-1 align-middle`}>{selected.size}</span>
                          )}
                        </span>
                        <span className={`hidden sm:block text-xs leading-snug truncate ${isActive ? "text-white/80" : "text-stone-500 dark:text-slate-400"}`}>
                          {t(`${tab}TabDesc`)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div ref={tabContentRef} className="scroll-mt-24" />

        {activeTab === "scan" && (
          <Suspense fallback={<TabFallback />}>
            <ScanTab lang={lang} t={t} dir={dir} userProfile={userProfile} isSubscribed={isSubscribed} onSubscribe={() => setShowSubModal(true)} session={session} creditStatus={creditStatus} refreshCredits={refreshCredits} />
          </Suspense>
        )}

        {activeTab === "weight" && (
          <Suspense fallback={<TabFallback />}>
            <WeightLossTab lang={lang} t={t} dir={dir} userProfile={userProfile} userId={session?.user?.id} />
          </Suspense>
        )}

        {/* Always mounted so step counting survives tab switches */}
        <div style={{ display: activeTab === "steps" ? undefined : "none" }}>
          <StepsTab t={t} dir={dir} lang={lang} weightKg={userProfile?.weight_kg ?? undefined} userId={session?.user?.id} isOnline={isOnline} onSignInRequest={() => setShowAuthModal(true)} player={musicPlayer} isWalking={isWalking} onWalkingStateChange={setIsWalking} />
        </div>


        {activeTab === "report" && (
          <DailyReport t={t} dir={dir} lang={lang} userProfile={userProfile} userId={session?.user?.id ?? null} />
        )}

        {activeTab === "meds" && (
          <MedicationTab refreshCredits={refreshCredits} session={session} dir={dir} lang={lang} onSignInRequest={() => setShowAuthModal(true)} />
        )}

        <div style={{ display: activeTab === "fridge" ? undefined : "none" }}>
          <div className="space-y-5">
            <div className={fridgeExpanded ? undefined : "hidden"}>
            <form onSubmit={e => {
              e.preventDefault();
              setFridgeSearch(fridgeInputVal);
              if (!fridgeInputVal.trim()) return;
              const q = normalizeAr(fridgeInputVal);
              const qEn = fridgeInputVal.trim().toLowerCase();
              const hasMatch = fridgeIngredients.some(i =>
                normalizeAr(i.nameAr).includes(q) || i.nameEn.toLowerCase().includes(qEn)
              );
              if (!hasMatch) {
                setActiveTab("recipes");
                setDishQuery(fridgeInputVal);
                searchDish(fridgeInputVal);
              }
            }} className="flex gap-2">
              <div className="relative flex-1">
                <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-slate-400 pointer-events-none ${dir === "rtl" ? "right-3.5" : "left-3.5"}`} />
                <input
                  value={fridgeInputVal}
                  onChange={e => setFridgeInputVal(e.target.value)}
                  placeholder={dir === "rtl" ? "ابحث عن مكوّن..." : "Search ingredient..."}
                  className={`w-full text-sm bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-600 rounded-xl py-2.5 text-stone-800 dark:text-slate-100 placeholder-stone-400 dark:placeholder-slate-500 outline-none focus:border-blue-500 dark:focus:border-blue-400 transition-colors shadow-sm ${dir === "rtl" ? "pr-9 pl-3" : "pl-9 pr-3"}`}
                />
              </div>
              <VoiceSearchButton lang={lang} onResult={(text) => { setFridgeInputVal(text); setFridgeSearch(text); }} />
              <button type="submit" disabled={!fridgeInputVal.trim()}
                className={`px-4 py-2.5 text-sm font-semibold rounded-xl transition-all shadow-sm border ${fridgeInputVal.trim() ? "bg-gradient-to-b from-[#0E2A52] to-[#08183A] border-blue-400/30 text-blue-200 hover:from-[#0E2A52]/90 hover:to-[#08183A]/90 shadow-[inset_0_1px_1px_rgba(255,255,255,0.12)]" : "bg-gradient-to-b from-[#0E2A52]/70 to-[#08183A]/70 border-blue-400/20 text-blue-300/50 cursor-not-allowed"}`}>
                {dir === "rtl" ? "بحث" : "Search"}
              </button>
              {fridgeSearch && (
                <button type="button" onClick={() => { setFridgeInputVal(""); setFridgeSearch(""); }}
                  className="px-3 py-2.5 bg-stone-100 dark:bg-slate-700 hover:bg-stone-200 dark:hover:bg-slate-600 text-stone-600 dark:text-slate-300 text-sm font-medium rounded-xl transition-colors">
                  {dir === "rtl" ? "مسح" : "Clear"}
                </button>
              )}
            </form>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex gap-2 flex-wrap items-center">
                <div className="flex items-center gap-1.5 bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-600 rounded-lg px-2 py-1 shadow-sm">
                  <Users className="w-3.5 h-3.5 text-blue-600" />
                  <button onClick={() => setFridgeServings(s => Math.max(1, s - 1))} className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-600/20 hover:bg-blue-200 dark:hover:bg-blue-600/30 text-blue-700 dark:text-blue-400 flex items-center justify-center font-bold text-base transition-colors leading-none">−</button>
                  <span className="text-sm font-bold text-stone-800 dark:text-slate-100 w-5 text-center tabular-nums">{fridgeServings}</span>
                  <button onClick={() => setFridgeServings(s => Math.min(20, s + 1))} className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-600/20 hover:bg-blue-200 dark:hover:bg-blue-600/30 text-blue-700 dark:text-blue-400 flex items-center justify-center font-bold text-base transition-colors leading-none">+</button>
                </div>
                <button onClick={() => setShowDietMenu(true)} disabled={selected.size === 0}
                  className={`text-sm px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 font-bold shadow-sm border ${selected.size > 0 ? "bg-gradient-to-b from-[#0E2A52] to-[#08183A] border-blue-400/30 text-blue-200 hover:from-[#0E2A52]/90 hover:to-[#08183A]/90 shadow-[inset_0_1px_1px_rgba(255,255,255,0.12)]" : "bg-gradient-to-b from-[#0E2A52]/70 to-[#08183A]/70 border-blue-400/20 text-blue-300/50 cursor-not-allowed"}`}>
                  <ChefHat className="w-3.5 h-3.5" />
                  {lang === "ar"
                    ? selected.size > 0 ? `${t("prepareDishes")} (${selected.size})` : t("prepareDishes")
                    : selected.size > 0 ? `${t("prepareDishes")} (${selected.size})` : t("prepareDishes")}
                </button>
                <button onClick={() => setShowCompare(true)} disabled={selected.size === 0}
                  className={`text-sm px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 font-bold shadow-sm border ${selected.size > 0 ? "bg-gradient-to-b from-[#0E2A52] to-[#08183A] border-blue-400/30 text-blue-200 hover:from-[#0E2A52]/90 hover:to-[#08183A]/90 shadow-[inset_0_1px_1px_rgba(255,255,255,0.12)]" : "bg-gradient-to-b from-[#0E2A52]/70 to-[#08183A]/70 border-blue-400/20 text-blue-300/50 cursor-not-allowed"}`}>
                  <PieChart className="w-3.5 h-3.5" />
                  {t("nutritionCompare")}
                </button>
                <button onClick={() => setSelected(new Set())} disabled={selected.size === 0}
                  className={`text-sm px-3 py-1.5 rounded-lg transition-all font-bold shadow-sm border ${selected.size > 0 ? "bg-gradient-to-b from-[#0E2A52] to-[#08183A] border-blue-400/30 text-blue-200 hover:from-[#0E2A52]/90 hover:to-[#08183A]/90 shadow-[inset_0_1px_1px_rgba(255,255,255,0.12)]" : "bg-gradient-to-b from-[#0E2A52]/70 to-[#08183A]/70 border-blue-400/20 text-blue-300/50 cursor-not-allowed"}`}>
                  {t("clearAll")}
                </button>
              </div>
            </div>

            {showCompare && selected.size > 0 && (
              <NutritionComparePanel
                lang={lang}
                dir={dir}
                imageOverrides={ingredientImageOverrides}
                picks={Array.from(selected)}
                onClose={() => setShowCompare(false)}
              />
            )}

            {fridgeSearch.trim() && groupedIngredients.length === 0 && (
              <div className="flex flex-col items-center gap-2 py-12 text-stone-400 dark:text-slate-500">
                <Search className="w-8 h-8 opacity-40" />
                <p className="text-sm font-medium">{t("noIngredientsMatch")} "{fridgeSearch}"</p>
              </div>
            )}

            {groupedIngredients.map(({ key, cat, items }) => {
              const selCount = items.filter(i => selected.has(i.id)).length;
              return (
              <div key={key} className="rounded-2xl overflow-hidden border border-stone-200 shadow-md bg-white">
                {/* Category Header */}
                <div className="px-4 py-2.5 flex items-center gap-2.5 border-b border-stone-100 bg-gradient-to-r from-slate-50 to-blue-50">
                  <span className="text-xl">{cat.icon}</span>
                  <h3 className="font-bold text-slate-700 text-sm tracking-wide">{cat[lang as keyof typeof cat]}</h3>
                  {key === "vegetables" && (
                    <span className="ms-1 text-[11px] font-medium text-blue-500 dark:text-blue-400/80">{t("pickIngredientsHint")}</span>
                  )}
                  {selCount > 0 && (
                    <span className="ms-1 px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold leading-none">{selCount}</span>
                  )}
                  <span className="ms-auto text-[11px] text-slate-400 dark:text-slate-500 font-medium">{selCount}/{items.length}</span>
                </div>
                {/* Ingredient Grid */}
                <div className="p-3 grid grid-cols-3 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                  {items.map(ing => {
                    const isSelected = selected.has(ing.id);
                    return (
                    <button key={ing.id} onClick={() => toggle(ing.id)}
                      className={`group relative flex flex-col items-center gap-1.5 pt-2 pb-2 px-1 rounded-xl border-4 transition-all duration-200 text-center cursor-pointer select-none overflow-hidden
                        ${isSelected
                          ? "border-4 border-[#08183A] bg-white shadow-lg shadow-blue-300/50 scale-[1.06] ring-2 ring-blue-400/40"
                          : "border border-[#08183A]/30 bg-white hover:border-[#08183A] hover:bg-blue-50/50 hover:scale-105 hover:shadow-sm"
                        }`}>
                      {isSelected && (
                        <span className="absolute top-1 end-1 bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-md z-10">
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </span>
                      )}
                      <div className="w-16 h-16 rounded-lg bg-white flex items-center justify-center overflow-hidden">
                        <img
                          src={ingredientImageOverrides[ing.id] ?? fluentEmojiMap[ing.id] ?? emojiUrl(ing.icon)}
                          alt={ing.nameAr}
                          className="w-16 h-16 object-contain group-hover:scale-110 transition-transform duration-200"
                          onError={e => { (e.target as HTMLImageElement).src = emojiUrl(ing.icon); }}
                        />
                      </div>
                      <span className={`text-[13px] font-semibold leading-tight ${isSelected ? "text-blue-700 dark:text-blue-300" : "text-slate-700"}`}>
                        {getIngredientName(ing, lang)}
                      </span>
                    </button>
                    );
                  })}
                </div>
              </div>
              );
            })}
            </div>
          </div>
        </div>

        {activeTab === "recipes" && (
          <div className="space-y-5">
            <form onSubmit={e => { e.preventDefault(); searchDish(); }} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute top-1/2 -translate-y-1/2 start-3.5 w-4 h-4 text-stone-400 dark:text-slate-400 pointer-events-none" />
                <input type="text" value={dishQuery} onChange={e => { setDishQuery(e.target.value); if (!e.target.value.trim()) setDishResult(null); }}
                  placeholder={t("dishSearch")}
                  className="w-full bg-white dark:bg-slate-700 border border-stone-200 dark:border-slate-600 rounded-xl ps-10 pe-4 py-2.5 text-sm text-stone-800 dark:text-slate-100 placeholder-stone-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-400 shadow-sm"
                  dir={dir}
                />
              </div>
              <VoiceSearchButton lang={lang} onResult={(text) => { setDishQuery(text); searchDish(text); }} />
              <button type="submit" disabled={!dishQuery.trim() || dishResult?.type === "loading"}
                className={`px-4 py-2.5 text-sm font-semibold rounded-xl transition-all shadow-sm border ${(!dishQuery.trim() || dishResult?.type === "loading") ? "bg-gradient-to-b from-[#0E2A52]/70 to-[#08183A]/70 border-blue-400/20 text-blue-300/50 cursor-not-allowed" : "bg-gradient-to-b from-[#0E2A52] to-[#08183A] border-blue-400/30 text-blue-200 hover:from-[#0E2A52]/90 hover:to-[#08183A]/90 shadow-[inset_0_1px_1px_rgba(255,255,255,0.12)]"}`}>
                {dishResult?.type === "loading"
                  ? <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  : t("dishSearchBtn")}
              </button>
              {dishResult !== null && (
                <button type="button" onClick={() => { setDishQuery(""); setDishResult(null); }}
                  className="px-3 py-2.5 bg-stone-100 dark:bg-slate-700 hover:bg-stone-200 dark:hover:bg-slate-600 text-stone-600 dark:text-slate-300 text-sm font-medium rounded-xl transition-colors">
                  {t("clearSearch")}
                </button>
              )}
            </form>

            <div className="space-y-2">
              <p className="text-xs font-semibold text-stone-400 dark:text-slate-500 uppercase tracking-wide px-0.5">
                {t("dietFilters")}
              </p>
              <FilterMarquee dir={dir} paused={dietFilter !== null}>
                {DIET_FILTERS.map(f => (
                  <button key={f.id} onClick={() => setDietFilter(prev => prev === f.id ? null : f.id)}
                    className={`shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border whitespace-nowrap transition-all ${dietFilter === f.id ? "bg-blue-600 border-blue-600 text-white shadow" : "bg-white dark:bg-slate-700 border-stone-200 dark:border-slate-600 text-stone-600 dark:text-slate-300"}`}>
                    <span>{f.icon}</span>
                    {f.label[lang]}
                    {dietFilter === f.id && <Check className="w-3 h-3 ms-0.5" />}
                  </button>
                ))}
              </FilterMarquee>
            </div>

            {dishResult === null && (
              <div className="space-y-2">
                <div className="flex items-center justify-between px-0.5">
                  <p className="text-xs font-semibold text-stone-400 dark:text-slate-500 uppercase tracking-wide">
                    {t("categories")}
                  </p>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                  {foodCategories.map(cat => (
                    <button key={cat} onClick={() => { setSelectedCategory(cat); }}
                      className={`flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl text-xs font-semibold border transition-all min-h-[2.75rem] ${selectedCategory === cat ? "bg-blue-600 border-blue-600 text-white shadow-md" : "bg-white dark:bg-slate-700 border-stone-200 dark:border-slate-600 text-stone-600 dark:text-slate-300 hover:border-blue-300 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-500"}`}>
                      <span className="text-base shrink-0">{categoryIcons[cat]}</span>
                      <span className="text-center leading-tight line-clamp-2">{cat === "all" ? t("allCategories") : t(cat)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {dishResult !== null && (
              <div className="space-y-4">
                {dishResult.type === "loading" && (() => {
                  const ITEMS = ["🥕","🧅","🍅","🥦","🧄","🥩","🍳","🧀","🌽","🥑","🍋","🫙"];
                  const POS = [
                    {x:4,y:8},{x:18,y:78},{x:32,y:12},{x:48,y:82},{x:63,y:6},
                    {x:76,y:72},{x:87,y:18},{x:10,y:48},{x:54,y:44},{x:91,y:56},{x:40,y:65},{x:70,y:32},
                  ];
                  const steps = [t("analysisStep1"), t("analysisStep2"), t("analysisStep3")];
                  return (
                    <div className="relative rounded-3xl overflow-hidden bg-gradient-to-b from-blue-50 to-sky-50 dark:from-slate-800 dark:to-slate-900 border-2 border-blue-200 dark:border-blue-600/30 p-8 flex flex-col items-center gap-7 min-h-[340px] justify-center">
                      {ITEMS.map((emoji, i) => (
                        <div key={i} className="absolute text-3xl pointer-events-none select-none"
                          style={{ left:`${POS[i].x}%`, top:`${POS[i].y}%`, opacity:0.18, animation:`food-float ${2.8+(i%4)*0.45}s ease-in-out ${i*0.25}s infinite` }}>
                          {emoji}
                        </div>
                      ))}
                      <div className="relative flex items-center justify-center w-40 h-40 z-10">
                        {[0,0.7,1.4].map((delay,i) => (
                          <div key={i} className="absolute inset-0 rounded-full border-2 border-blue-500/50"
                            style={{ animation:`scan-ring 2.4s ease-out ${delay}s infinite` }} />
                        ))}
                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 via-green-500 to-sky-600 flex items-center justify-center relative"
                          style={{ animation:"orb-breathe-green 2.6s ease-in-out infinite" }}>
                          <div className="absolute inset-0 rounded-full bg-white/10" />
                          <div className="absolute inset-2 rounded-full border-2 border-dashed border-white/30"
                            style={{ animation:"spin 5s linear infinite" }} />
                          <span className="text-4xl relative z-10 select-none" style={{ animation:"orb-breathe 3s ease-in-out infinite" }}>🍳</span>
                        </div>
                      </div>
                      <div className="text-center space-y-1.5 z-10">
                        <p className="text-lg font-black text-stone-800 dark:text-slate-100 tracking-tight">{t("dishNotFoundAi").split("،")[0]}</p>
                        <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 min-h-[1.2rem]">{steps[searchMsg]}</p>
                      </div>
                      <div className="w-64 z-10 space-y-3">
                        <div className="h-2 bg-blue-100 dark:bg-blue-900/40 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-sky-500"
                            style={{ animation:"gen-progress 12s ease-out forwards" }} />
                        </div>
                        <div className="flex justify-center gap-3">
                          {steps.map((_, i) => (
                            <div key={i} className={`rounded-full transition-all duration-500 ${searchMsg===i?"w-5 h-2 bg-blue-600":searchMsg>i?"w-2 h-2 bg-blue-500":"w-2 h-2 bg-stone-200 dark:bg-slate-700"}`} />
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 z-10">
                        {[0,1,2,3,4].map(i => (
                          <div key={i} className="w-2.5 h-2.5 rounded-full bg-blue-600 dark:bg-blue-500"
                            style={{ animation:`dot-wave 1.3s ease-in-out ${i*0.14}s infinite` }} />
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {dishResult.type === "static" && (() => {
                  const activeDiet = DIET_FILTERS.find(f => f.id === dietFilter);
                  const filtered = activeDiet ? dishResult.recipes.filter(r => activeDiet.passes(r)) : dishResult.recipes;
                  return filtered.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {filtered.map(recipe => (
                        <RecipeCard key={recipe.id} recipe={recipe} lang={lang} t={t} onView={openRecipe}
                          isSaved={savedIds.has(recipe.id)} onSave={() => toggleSave(recipe.id)} onShare={() => shareRecipe(recipe)} imageOverride={recipeImageOverrides[recipe.id]} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 shadow-sm">
                      <Filter className="w-10 h-10 text-stone-300 dark:text-slate-500 mx-auto mb-3" />
                      <p className="text-stone-500 dark:text-slate-400 text-sm">{t("noFilterResults")}</p>
                    </div>
                  );
                })()}

                {dishResult.type === "ai" && (
                  <AiRecipeCard recipe={dishResult.recipe} onClose={() => setDishResult(null)} t={t} dir={dir} lang={lang} />
                )}

                {dishResult.type === "saved_ai" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm font-semibold text-stone-600 dark:text-slate-300">
                        <History className="w-4 h-4 text-blue-600" />
                        {t("savedAiRecipes")} ({dishResult.recipes.length})
                      </span>
                      <button onClick={() => setDishResult(null)} className="p-1.5 rounded-full hover:bg-stone-100 dark:hover:bg-slate-700 text-stone-400 dark:text-slate-400 transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {dishResult.recipes.map((recipe, i) => (
                      <AiRecipeCard key={i} recipe={recipe} onClose={() => setDishResult(null)} t={t} dir={dir} lang={lang} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {dishResult === null && (
              <>
                {menuRecipes.length === 0 ? (
                  <div className="space-y-4">
                    {searchHistory.length > 0 ? (
                      <div className="space-y-4">
                        {dietFilter === "glutenFree" && (
                          <div className="text-center py-8 bg-amber-50 dark:bg-amber-500/10 rounded-2xl border border-amber-200 dark:border-amber-500/20">
                            <Filter className="w-10 h-10 text-amber-300 dark:text-amber-600 mx-auto mb-2" />
                            <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">{t("noResultsFound")}</p>
                            <p className="text-xs text-amber-600 dark:text-amber-500 mt-2 max-w-xs mx-auto leading-relaxed">{t("glutenFreeEmptyState")}</p>
                          </div>
                        )}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 shadow-sm p-5">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-stone-700 dark:text-slate-300 text-sm flex items-center gap-2">
                              <History className="w-4 h-4 text-stone-400 dark:text-slate-500" />
                              {t("recentSearches")}
                            </h3>
                            <button onClick={async () => {
                              await supabase.from("search_history").delete().eq("device_id", getDeviceId());
                              setSearchHistory([]);
                            }} className="text-xs text-stone-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                              {t("clearAll2")}
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {searchHistory.map(item => (
                              <button key={item.id} onClick={() => { setDishQuery(item.query); searchDish(item.query); }}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-50 dark:bg-slate-700 hover:bg-blue-50 dark:hover:bg-blue-600/20 border border-stone-200 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-500 text-stone-600 dark:text-slate-300 hover:text-blue-700 dark:hover:text-blue-300 rounded-full text-sm transition-all">
                                <Search className="w-3 h-3 opacity-50" />
                                {item.query}
                                {item.result_type === "ai" && <Sparkles className="w-3 h-3 text-blue-500" />}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 shadow-sm">
                        <Filter className="w-12 h-12 text-stone-300 dark:text-slate-600 mx-auto mb-3" />
                        <p className="text-stone-500 dark:text-slate-400">{t("noResultsFound")}</p>
                        {dietFilter === "glutenFree" && (
                          <p className="text-xs text-stone-400 dark:text-slate-500 mt-2 max-w-xs mx-auto leading-relaxed">{t("glutenFreeEmptyState")}</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {menuRecipes.map(recipe => (
                      <RecipeCard key={recipe.id} recipe={recipe} lang={lang} t={t} onView={openRecipe}
                        isSaved={savedIds.has(recipe.id)} onSave={() => toggleSave(recipe.id)} onShare={() => shareRecipe(recipe)} imageOverride={recipeImageOverrides[recipe.id]} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        </UserDataProvider>
        </div>
      </main>

      {/* Recipe modal */}
      {activeRecipe && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm" onClick={() => { setActiveRecipe(null); setRecipeHealthScore(null); }} />
          <div className="relative bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl" dir={dir}>
            <div className="sticky top-0 bg-white dark:bg-slate-800 z-10 flex items-center justify-between px-5 pt-5 pb-3 border-b border-stone-100 dark:border-slate-700">
              <h2 className="text-xl font-bold text-stone-800 dark:text-slate-100">{getRecipeName(activeRecipe, lang)}</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => shareRecipe(activeRecipe)} className="p-2 rounded-full hover:bg-blue-50 dark:hover:bg-blue-500/20 text-blue-500 dark:text-blue-400 transition-colors">
                  <Share2 className="w-5 h-5" />
                </button>
                <button onClick={() => toggleSave(activeRecipe.id)}
                  className={`p-2 rounded-full transition-colors ${savedIds.has(activeRecipe.id) ? "bg-blue-50 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 hover:bg-red-50 dark:hover:bg-red-500/20 hover:text-red-500 dark:hover:text-red-400" : "hover:bg-blue-50 dark:hover:bg-blue-600/20 text-stone-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-500"}`}>
                  {savedIds.has(activeRecipe.id) ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
                </button>
                <button onClick={() => { setActiveRecipe(null); setRecipeHealthScore(null); }} className="p-2 rounded-full hover:bg-stone-100 dark:hover:bg-slate-700 text-stone-500 dark:text-slate-400 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <img src={activeRecipe.image} alt={getRecipeName(activeRecipe, lang)} referrerPolicy="no-referrer" className="w-full h-52 object-cover" />

            <div className="grid grid-cols-2 gap-3 p-5">
              <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-600/15 rounded-xl p-3">
                <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm text-stone-700 dark:text-slate-200">{activeRecipe.time} {t("minutes")}</span>
              </div>
              <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-500/15 rounded-xl p-3">
                <Users className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
                <span className="text-sm text-stone-600 dark:text-slate-300 flex-1">{servingsLabel(lang, activeServings)}</span>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setActiveServings(s => Math.max(1, s - 1))} className="w-6 h-6 rounded-full bg-blue-200 dark:bg-blue-500/20 hover:bg-blue-300 dark:hover:bg-blue-500/30 text-blue-800 dark:text-blue-400 flex items-center justify-center font-bold text-sm transition-colors leading-none">−</button>
                  <button onClick={() => setActiveServings(s => Math.min(20, s + 1))} className="w-6 h-6 rounded-full bg-blue-200 dark:bg-blue-500/20 hover:bg-blue-300 dark:hover:bg-blue-500/30 text-blue-800 dark:text-blue-400 flex items-center justify-center font-bold text-sm transition-colors leading-none">+</button>
                </div>
              </div>
            </div>

            <div className="px-5 pb-4">
              <MacroDonut
                calories={activeRecipe.nutrition.calories}
                protein={activeRecipe.nutrition.protein}
                carbs={activeRecipe.nutrition.carbs}
                fat={activeRecipe.nutrition.fat}
                sugar={activeRecipe.nutrition.sugar}
                fiber={activeRecipe.nutrition.fiber ?? 0}
                sodium={activeRecipe.nutrition.sodium ?? 0}
                t={t}
                dir={dir}
              />
              {activeRecipe.ingredientCompleteness === "unverified" && (
                <div className="mt-2 flex items-start gap-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl p-3">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">{t("nutritionBasisUnverified")}</p>
                </div>
              )}
            </div>

            <div className="px-5 pb-4">
              {!recipeHealthScore && !recipeHealthScoreLoading && (
                <button
                  onClick={() => fetchRecipeHealthScore(activeRecipe)}
                  disabled={healthScoreFetching}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm bg-blue-50 dark:bg-blue-600/15 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-600/25 border border-blue-200 dark:border-blue-600/30 transition-colors disabled:opacity-50"
                >
                  <Award className="w-4 h-4" />
                  {t("getHealthScore")}
                </button>
              )}
              {(recipeHealthScoreLoading || recipeHealthScore) && (
                <HealthScoreInlineCard result={recipeHealthScore} loading={recipeHealthScoreLoading} t={t} dir={dir} />
              )}
            </div>

            <div className="px-5 pb-4">
              <h3 className="font-bold text-stone-800 dark:text-slate-100 mb-3 flex items-center gap-2">
                <Star className="w-4 h-4 text-blue-500" />
                {t("ingredients")}
              </h3>
              <div className="space-y-2">
                {[...activeRecipe.requiredIngredients, ...activeRecipe.optionalIngredients].map(id => {
                  const ing = fridgeIngredients.find(i => i.id === id);
                  if (!ing) return null;
                  const isRequired = activeRecipe.requiredIngredients.includes(id);
                  const qty = activeRecipe.ingredientQuantities?.[id];
                  const isUnknownQty = qty && qty.amount === null;
                  const scaledAmt = qty ? scaleAmount(qty.amount, activeRecipe.servings, activeServings) : null;
                  return (
                    <div key={id} className={`flex items-center justify-between px-3 py-2.5 rounded-xl border ${isRequired ? "bg-blue-50 dark:bg-blue-600/15 border-blue-100 dark:border-blue-600/30" : "bg-stone-50 dark:bg-slate-700 border-stone-100 dark:border-slate-600"}`}>
                      <span className={`flex items-center gap-2 text-sm font-medium ${isRequired ? "text-stone-800 dark:text-slate-100" : "text-stone-500 dark:text-slate-400"}`}>
                        <span className="text-base">{ing.icon}</span>
                        <span>{getIngredientName(ing, lang)}</span>
                        {!isRequired && <span className="text-xs text-stone-400 dark:text-slate-500 font-normal">({t("optional")})</span>}
                      </span>
                      {isUnknownQty ? (
                        <span className={`text-sm font-bold tabular-nums opacity-60 ${isRequired ? "text-blue-600 dark:text-blue-400" : "text-stone-400 dark:text-slate-500"}`}>
                          {qty!.unit}
                        </span>
                      ) : scaledAmt && (
                        <span className={`text-sm font-bold tabular-nums ${isRequired ? "text-blue-600 dark:text-blue-400" : "text-stone-400 dark:text-slate-500"}`}>
                          {scaledAmt} <span className="font-normal text-xs">{qty!.unit}</span>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="px-5 pb-4">
              <h3 className="font-bold text-stone-800 dark:text-slate-100 mb-3 flex items-center gap-2">
                <ChefHat className="w-4 h-4 text-blue-500" />
                {t("steps")}
              </h3>
              <ol className="space-y-3">
                {getRecipeSteps(activeRecipe, lang).map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center font-bold">{i + 1}</span>
                    <p className="text-stone-700 dark:text-slate-300 text-sm leading-relaxed">{step}</p>
                  </li>
                ))}
              </ol>
            </div>

            {(() => {
              const ovenTemp = getOvenTemp(activeRecipe, lang);
              const tips = getCookingTips(activeRecipe, lang);
              const serving = getServingTips(activeRecipe, lang);
              if (!ovenTemp && tips.length === 0 && serving.length === 0) return null;
              return (
                <div className="px-5 pb-2 space-y-4">
                  {ovenTemp && (
                    <div className="bg-orange-50 dark:bg-orange-500/10 rounded-2xl p-4 border border-orange-100 dark:border-orange-500/20">
                      <h3 className="flex items-center gap-2 font-bold text-stone-800 dark:text-slate-100 text-sm mb-2">
                        <Thermometer className="w-4 h-4 text-orange-500" />
                        {t("ovenTemp")}
                      </h3>
                      <p className="text-stone-700 dark:text-slate-300 text-sm leading-relaxed">{ovenTemp}</p>
                    </div>
                  )}
                  {tips.length > 0 && (
                    <div className="bg-amber-50 dark:bg-amber-500/10 rounded-2xl p-4 border border-amber-100 dark:border-amber-500/20">
                      <h3 className="flex items-center gap-2 font-bold text-stone-800 dark:text-slate-100 text-sm mb-2">
                        <Flame className="w-4 h-4 text-amber-500" />
                        {t("cookingTips")}
                      </h3>
                      <ul className="space-y-2">
                        {tips.map((tip, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="flex-shrink-0 w-1.5 h-1.5 mt-2 bg-amber-500 rounded-full" />
                            <p className="text-stone-700 dark:text-slate-300 text-sm leading-relaxed">{tip}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {serving.length > 0 && (
                    <div className="bg-emerald-50 dark:bg-emerald-500/10 rounded-2xl p-4 border border-emerald-100 dark:border-emerald-500/20">
                      <h3 className="flex items-center gap-2 font-bold text-stone-800 dark:text-slate-100 text-sm mb-2">
                        <UtensilsCrossed className="w-4 h-4 text-emerald-500" />
                        {t("servingTips")}
                      </h3>
                      <ul className="space-y-2">
                        {serving.map((s, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="flex-shrink-0 w-1.5 h-1.5 mt-2 bg-emerald-500 rounded-full" />
                            <p className="text-stone-700 dark:text-slate-300 text-sm leading-relaxed">{s}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="px-5 pb-6 flex flex-col gap-3">
              <button
                onClick={() => saveFridgeRecipeToDiary(activeRecipe, recipeHealthScore)}
                disabled={recipeDiarySaved.has(activeRecipe.id)}
                className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm transition-all ${
                  recipeDiarySaved.has(activeRecipe.id)
                    ? "bg-stone-100 dark:bg-slate-700 text-stone-400 dark:text-slate-500 cursor-default"
                    : "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-md hover:shadow-lg hover:scale-[1.01]"
                }`}
              >
                <BookOpen className="w-4 h-4" />
                {recipeDiarySaved.has(activeRecipe.id) ? t("savedToDiary") : t("saveToDiary")}
                {recipeHealthScore && !recipeDiarySaved.has(activeRecipe.id) && (
                  <span className="text-[11px] font-black w-6 h-6 rounded-full flex items-center justify-center ml-1 bg-white/30 text-white">
                    {recipeHealthScore.healthy_score}
                  </span>
                )}
              </button>
              <div className="flex gap-3">
                <button onClick={() => shareRecipe(activeRecipe)} className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-xl font-medium text-sm transition-colors">
                  <Share2 className="w-4 h-4" />
                  {t("shareRecipe")}
                </button>
                <button onClick={() => toggleSave(activeRecipe.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-colors ${savedIds.has(activeRecipe.id) ? "bg-stone-100 dark:bg-slate-700 hover:bg-red-50 dark:hover:bg-red-500/20 text-stone-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 border border-stone-200 dark:border-slate-600 hover:border-red-200 dark:hover:border-red-500/50" : "bg-blue-600 hover:bg-blue-700 text-white"}`}>
                  {savedIds.has(activeRecipe.id)
                    ? <><Trash2 className="w-4 h-4" />{t("removeFromSaved")}</>
                    : <><Bookmark className="w-4 h-4" />{t("saveRecipe")}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

    </>
  );
}
